import { AgentSchema } from "@/v2/types/client";
import {
  DesignOption,
  DesktopViewOption,
  DisplayMode,
  DisplayPositionOption,
  HeaderDisplayOption,
} from "@/v2/types/schema";
import { AGENT_VERSION } from "@/v2/utils/constants";
import { loadLocalPreviewAgentSettings } from "@/v2/libs/localPreviewSettings";

const localPreviewOverrides: Partial<AgentSchema> = {
  version: AGENT_VERSION.NonStudio,
  agent_name: "PITALIy",
  description:
    "こんにちは！なんでも相談してね⭐️\n料金、導入方法、資料請求、無料相談について案内できます。",
  dark_mode: false,
  theme_color: "default",
  font_family: "noto_sans_jp",
  button_theme: "default",
  image_url: "",
  display_mode: DisplayMode.CHARACTER,
  header_display_option: HeaderDisplayOption.ICON_AND_NAME,
  desktop_view_option: DesktopViewOption.FULL_SCREEN,
  background_style_type: "gradient",
  background_color: "#8DA9D6",
  background_gradient: "linear-gradient(135deg, #8DA9D6 0%, #B7C9E8 100%)",
  character_position: -100,
  character_size: 100,
  character_position_sp: -100,
  character_size_sp: 100,
  talking_character_position: -100,
  talking_character_size: 100,
  talking_character_position_sp: -100,
  talking_character_size_sp: 100,
  random_character_enabled: false,
  character_hint_enabled: true,
  character_hint_mode: "auto",
  character_hint_interval_seconds: 20,
  character_hint_messages: [
    "まずは30秒だけ、あなたに合う使い方を一緒に見つけます。✨",
    "資料や料金も、気になるところから案内できます。",
  ],
  floating_feature_text_color: "white",
  design_option: DesignOption.BUTTON_THEME,
  display_position_option: DisplayPositionOption.RIGHT,
  line_link_flag: false,
  chat_login_flag: false,
  voice_mode_flag: true,
  voice_mode_greeting: "こんにちは！音声でも質問できます。",
  quick_reply_messages: ["料金について", "導入までの流れ", "資料を見たい"],
  first_fixed_message: "こんにちは！なんでも相談してね⭐️",
  start_consent_required: false,
  start_consent_label: "利用規約とプライバシーポリシーに同意する",
  start_terms_text: "",
  start_privacy_text: "",
  paid_chat_settings: undefined,
  model_streaming: true,
  use_form: false,
  faq_flag: true,
  faq_tags: ["料金", "導入", "資料請求"],
  max_conversation: 999,
  link_settings: [],
  forms_generals: [],
  gif_datas: [],
};

export const shouldUseLocalPreview = () =>
  import.meta.env.DEV && import.meta.env.VITE_DISABLE_LOCAL_PREVIEW !== "true";

export const shouldForceLocalPreview = () =>
  shouldUseLocalPreview() && import.meta.env.VITE_FORCE_LOCAL_PREVIEW === "true";

const isAdminDesignPreview = () => {
  if (typeof window === "undefined") return false;
  if (window.self === window.top) return false;

  return new URLSearchParams(window.location.search).get("preview") === "admin-design";
};

export const createLocalPreviewAgent = (agentId: string): AgentSchema => {
  const now = new Date().toISOString();

  return {
    agent_name: "PITALIy",
    email: "local-preview@pitaliy.dev",
    description: "",
    created_at: now,
    chat_settings_id: `local-chat-settings-${agentId}`,
    dark_mode: false,
    theme_color: "default",
    font_family: "noto_sans_jp",
    button_theme: "default",
    image_url: "",
    display_mode: DisplayMode.CHARACTER,
    header_display_option: HeaderDisplayOption.ICON_AND_NAME,
    header_image: undefined,
    background_pc: undefined,
    background_sp: undefined,
    background_style_type: "gradient",
    background_color: "#8DA9D6",
    background_gradient: "linear-gradient(135deg, #8DA9D6 0%, #B7C9E8 100%)",
    desktop_view_option: DesktopViewOption.FULL_SCREEN,
    character_proportion: "two-head",
    character_position: -100,
    character_size: 100,
    character_position_sp: -100,
    character_size_sp: 100,
    talking_character_position: -100,
    talking_character_size: 100,
    talking_character_position_sp: -100,
    talking_character_size_sp: 100,
    random_character_enabled: false,
    character_hint_enabled: true,
    character_hint_mode: "auto",
    character_hint_interval_seconds: 20,
    character_hint_messages: [
      "まずは30秒だけ、あなたに合う使い方を一緒に見つけます。✨",
      "資料や料金も、気になるところから案内できます。",
    ],
    floating_feature_text_color: "white",
    design_option: DesignOption.BUTTON_THEME,
    banner_pc: undefined,
    banner_sp: undefined,
    display_position_option: DisplayPositionOption.RIGHT,
    line_client_id: null,
    line_return_uri: "",
    line_link_flag: false,
    chat_login_flag: false,
    voice_mode_flag: true,
    voice_mode_greeting: "こんにちは！音声でも質問できます。",
    remaining_conversation: 999,
    voice_id: "",
    quick_reply_messages: ["料金について", "導入までの流れ", "資料を見たい"],
    first_fixed_message: "こんにちは！なんでも相談してね⭐️",
    start_consent_required: false,
    start_consent_label: "利用規約とプライバシーポリシーに同意する",
    start_terms_text: "",
    start_privacy_text: "",
    paid_chat_settings: undefined,
    model_streaming: true,
    use_form: false,
    faq_flag: true,
    faq_tags: ["料金", "導入", "資料請求"],
    max_conversation: 999,
    link_settings: [],
    gif_datas: [],
    knowledge_data_store_id: `local-knowledge-${agentId}`,
    forms_generals: [],
    ...localPreviewOverrides,
  };
};

export const applyLocalPreviewAgent = (
  agent: AgentSchema | null,
  agentId: string,
  remoteSettings: Partial<AgentSchema> = {},
): AgentSchema => {
  const fallback = createLocalPreviewAgent(agentId);
  const shouldApplyCookiePreview =
    agentId === "00000000-0000-4000-8000-000000000001" ||
    shouldForceLocalPreview() ||
    isAdminDesignPreview();
  const localSettings = {
    ...remoteSettings,
    ...(shouldApplyCookiePreview ? loadLocalPreviewAgentSettings(agentId) : {}),
  };

  return {
    ...fallback,
    ...(agent ?? {}),
    ...localSettings,
    version: AGENT_VERSION.NonStudio,
    chat_settings_id: agent?.chat_settings_id || fallback.chat_settings_id,
    knowledge_data_store_id:
      agent?.knowledge_data_store_id || fallback.knowledge_data_store_id,
    remaining_conversation:
      agent?.remaining_conversation ?? fallback.remaining_conversation,
    gif_datas: localSettings.gif_datas?.length
      ? localSettings.gif_datas
      : agent?.gif_datas?.length
      ? agent.gif_datas
      : fallback.gif_datas,
    quick_reply_messages: localSettings.quick_reply_messages?.length
      ? localSettings.quick_reply_messages
      : agent?.quick_reply_messages?.length
      ? agent.quick_reply_messages
      : fallback.quick_reply_messages,
    forms_generals: agent?.forms_generals?.length
      ? agent.forms_generals
      : fallback.forms_generals,
    link_settings: agent?.link_settings?.length
      ? agent.link_settings
      : fallback.link_settings,
  };
};
